/**
 * Canonical opening-tag format for ContentUnit `raw:` styles.
 *
 * All four parsers (edge, core HTML walker, core DOM walker, PHP) must
 * produce byte-identical output for the same source element. Rules:
 *   - tag name lowercased
 *   - attribute names lowercased
 *   - attribute values always wrapped in double-quotes
 *   - attribute insertion order preserved from source
 *   - boolean attrs emitted as `attr=""`
 *   - void elements use the same form (no self-closing slash, no close tag emitted)
 *
 * Attribute values are stored byte-for-byte as captured from the source
 * (the source's own entity encoding is preserved — already valid HTML).
 * The serializer only re-escapes `"` → `&quot;` so the chosen double-quote
 * delimiter stays well-formed when the source used single quotes.
 */

export const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

function escapeAttrValue(value: string): string {
  return value.replace(/"/g, '&quot;');
}

/**
 * Build the canonical opening tag string. Used by parsers to produce
 * the `raw:<...>` style content.
 */
export function serializeOpeningTag(
  tagName: string,
  attrs: Record<string, string>,
): string {
  const tag = tagName.toLowerCase();
  let result = `<${tag}`;
  for (const [name, value] of Object.entries(attrs)) {
    result += ` ${name.toLowerCase()}="${escapeAttrValue(value)}"`;
  }
  result += '>';
  return result;
}

/**
 * Extract the tag name from a canonical opening tag string.
 * `<span class="x">` → `span`. Returns lowercase.
 */
export function extractTagName(rawOpenTag: string): string {
  const end = rawOpenTag.search(/[\s>]/);
  return rawOpenTag.slice(1, end === -1 ? rawOpenTag.length : end).toLowerCase();
}

/**
 * Return the raw opening tag (the portion after `raw:`) from a style array,
 * or null if no `raw:` style is present.
 */
export function readRawStyle(styles: string[]): string | null {
  for (const s of styles) {
    if (s.startsWith('raw:')) return s.slice(4);
  }
  return null;
}
